/**
 * Banner template rules, ported by hand from
 * packages/api/src/modules/collection-points/banner-template-rules.ts so the
 * editor can flag an issue as the person types. The messages are the API's,
 * word for word; the server check (POST /v1/banner-templates/check) is the
 * authority and runs after this one.
 */

/** Slots the script fills. Text slots are escaped; fragment slots receive built elements. */
export const TEMPLATE_SLOTS = {
  title: 'text', body: 'text', tenant: 'text',
  buttons: 'fragment', categories: 'fragment', notice_link: 'fragment', language: 'fragment',
  accessibility: 'fragment', logo: 'fragment', close: 'fragment', powered_by: 'fragment',
} as const;
export type TemplateSlot = keyof typeof TEMPLATE_SLOTS;

/** The two layers a template must mark: data-wk-layer="ask" and data-wk-layer="prefs". */
export const TEMPLATE_LAYERS = ['ask', 'prefs'] as const;

export const TEMPLATE_LIMITS = { html: 20_000, css: 20_000 } as const;

export const BUILT_IN_TEMPLATE_KEYS = ['card', 'strip', 'wall', 'sheet', 'toast', 'brand'] as const;
export type BuiltInTemplateKey = typeof BUILT_IN_TEMPLATE_KEYS[number];

export interface TemplateIssue { code: string; message: string; where?: 'html' | 'css' }

const SLOT_RE = /\{\{\s*([a-z_]+)\s*\}\}/g;

/** Slots used in a block of HTML. */
export function slotsIn(html: string): Set<string> {
  const found = new Set<string>();
  for (const m of html.matchAll(SLOT_RE)) found.add(m[1]!);
  return found;
}

/** The HTML of one layer: the element carrying data-wk-layer="<name>" with its contents. Best effort, no parser. */
export function layerHtml(html: string, layer: string): string | null {
  const re = new RegExp(`<([a-z][a-z0-9-]*)[^>]*data-wk-layer=["']${layer}["'][^>]*>([\\s\\S]*?)</\\1>\\s*(?=<[a-z][^>]*data-wk-layer=|$)`, 'i');
  const m = html.match(re);
  return m ? m[0] : null;
}

/**
 * What a template must not contain. These are refused outright, never
 * stripped, so the author sees what was wrong.
 */
export function unsafeHtml(html: string): TemplateIssue[] {
  const issues: TemplateIssue[] = [];
  const rules: Array<[RegExp, string, string]> = [
    [/<\s*script\b/i, 'script', 'Script tags are not allowed.'],
    [/\son[a-z]+\s*=/i, 'event_handler', 'Inline event handlers (on…=) are not allowed.'],
    [/javascript\s*:/i, 'javascript_url', 'javascript: addresses are not allowed.'],
    [/<\s*(iframe|object|embed|link|meta|base|form|input|textarea|select)\b/i, 'element', 'iframe, object, embed, link, meta, base and form controls are not allowed; the script supplies every control.'],
    [/<\s*style\b/i, 'style_tag', 'Put styles in the CSS field, not in a style tag.'],
    [/\b(src|href|srcset|poster|data)\s*=\s*["']?\s*(https?:)?\/\//i, 'external_resource', 'No address on another host; the banner loads nothing from outside the page.'],
    [/<\s*img\b(?![^>]*\{\{\s*logo\s*\}\})[^>]*>/i, 'image', 'Images are not allowed except the {{logo}} slot.'],
  ];
  for (const [re, code, message] of rules) if (re.test(html)) issues.push({ code, message, where: 'html' });
  return issues;
}

export function unsafeCss(css: string): TemplateIssue[] {
  const issues: TemplateIssue[] = [];
  const rules: Array<[RegExp, string, string]> = [
    [/@import\b/i, 'css_import', '@import is not allowed.'],
    [/url\(\s*["']?\s*(https?:)?\/\//i, 'css_external', 'url() to another host is not allowed.'],
    [/expression\s*\(/i, 'css_expression', 'expression() is not allowed.'],
    [/behavior\s*:/i, 'css_behavior', 'behavior: is not allowed.'],
    [/-moz-binding/i, 'css_binding', '-moz-binding is not allowed.'],
    [/<\s*\/?\s*style/i, 'css_tag', 'No HTML in the CSS field.'],
  ];
  for (const [re, code, message] of rules) if (re.test(css)) issues.push({ code, message, where: 'css' });
  return issues;
}

/**
 * Structure the law needs, checked on the text alone. Size and visibility are
 * measured by the script in the preview and posted back.
 */
export function templateStructure(html: string): TemplateIssue[] {
  const issues: TemplateIssue[] = [];
  const ask = layerHtml(html, 'ask');
  const prefs = layerHtml(html, 'prefs');
  if (!ask) issues.push({ code: 'layer_ask', message: 'The first layer is missing: an element with data-wk-layer="ask".', where: 'html' });
  if (!prefs) issues.push({ code: 'layer_prefs', message: 'The preferences layer is missing: an element with data-wk-layer="prefs".', where: 'html' });
  if (ask) {
    const s = slotsIn(ask);
    if (!s.has('buttons')) issues.push({ code: 'ask_buttons', message: 'The first layer needs the {{buttons}} slot: Accept all and Reject all together.', where: 'html' });
    if (!s.has('notice_link')) issues.push({ code: 'ask_notice', message: 'The first layer needs the {{notice_link}} slot.', where: 'html' });
    if (!s.has('body') && !s.has('title')) issues.push({ code: 'ask_text', message: 'The first layer needs {{title}} or {{body}}.', where: 'html' });
  }
  if (prefs) {
    const s = slotsIn(prefs);
    if (!s.has('categories')) issues.push({ code: 'prefs_categories', message: 'The preferences layer needs the {{categories}} slot.', where: 'html' });
    if (!s.has('buttons')) issues.push({ code: 'prefs_buttons', message: 'The preferences layer needs the {{buttons}} slot.', where: 'html' });
  }
  for (const name of slotsIn(html)) {
    if (!(name in TEMPLATE_SLOTS)) issues.push({ code: 'unknown_slot', message: `Unknown slot {{${name}}}. Slots: ${Object.keys(TEMPLATE_SLOTS).join(', ')}.`, where: 'html' });
  }
  if (html.length > TEMPLATE_LIMITS.html) issues.push({ code: 'html_size', message: `HTML is over ${TEMPLATE_LIMITS.html} characters.`, where: 'html' });
  return issues;
}

/** Everything the API checks before a template is saved or published. */
export function checkTemplate(html: string, css: string): TemplateIssue[] {
  const issues = [...unsafeHtml(html), ...unsafeCss(css), ...templateStructure(html)];
  if (css.length > TEMPLATE_LIMITS.css) issues.push({ code: 'css_size', message: `CSS is over ${TEMPLATE_LIMITS.css} characters.`, where: 'css' });
  return issues;
}
